import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { CheckCircle2, Circle, X, Rocket } from "lucide-react";

const STORAGE_KEY = "dp-onboarding-hidden";

/**
 * Onboarding checklist widget shown on Dashboard for new LDs.
 * - Fetches /api/ld/onboarding-status
 * - Auto-hides once >=80% done (or on user dismiss).
 * - Persisted dismiss stored per-LD in localStorage.
 */
export const OnboardingChecklist = () => {
  const { user, token } = useAuth();
  const [status, setStatus] = useState(null);
  const [hidden, setHidden] = useState(false);

  const storageKey = user?.ld_id ? `${STORAGE_KEY}-${user.ld_id}` : null;

  useEffect(() => {
    if (!token || !user?.ld_id || user?.vloga === "super_admin") return;
    try {
      const dismissed = storageKey ? localStorage.getItem(storageKey) : null;
      if (dismissed === "1") { setHidden(true); return; }
    } catch (_) { /* ignore */ }
    axios.get(`${API}/ld/onboarding-status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setStatus(r.data))
      .catch(() => { /* silent */ });
  }, [token, user?.ld_id, user?.vloga, storageKey]);

  if (!status || hidden) return null;
  if (status.percent >= 80) return null; // Auto-hide once mostly done

  const dismiss = () => {
    try { if (storageKey) localStorage.setItem(storageKey, "1"); } catch (_) { /* ignore */ }
    setHidden(true);
  };

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-amber-50/40" data-testid="onboarding-checklist">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
              <Rocket className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Prvi koraki v aplikaciji</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                {status.done}/{status.total} zaključenih ({status.percent}%)
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 -mr-2 -mt-2"
            onClick={dismiss}
            data-testid="onboarding-dismiss"
            title="Skrij"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        {/* Progress bar */}
        <div className="mt-2 h-1.5 bg-primary/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-500"
            style={{ width: `${status.percent}%` }}
            data-testid="onboarding-progress"
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-1">
        {status.items.map(item => (
          <div
            key={item.id}
            className={`flex items-center gap-2 py-1.5 text-sm ${item.done ? "text-muted-foreground line-through" : "text-foreground"}`}
            data-testid={`onboarding-item-${item.id}`}
          >
            {item.done
              ? <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
              : <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            }
            <span>{item.label}</span>
            {typeof item.value === "number" && item.value > 0 && (
              <span className="ml-auto text-xs text-muted-foreground">({item.value})</span>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default OnboardingChecklist;
